import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  Calendar,
  Crown,
  ExternalLink,
  Gauge,
  KeyRound,
  Mail,
  Plug,
  ShieldQuestion,
  Sparkles,
} from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { Badge } from "@/components/ui/badge";
import { tierBadgeColor, tierBadgeLabel } from "@/lib/tierBadge";
import { roleBadgeColor, roleBadgeLabel } from "@/lib/roleBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  getAutomationPause,
  getUserQuotas,
  listUsers,
  setAutomationPause,
  type AccountTier,
  type AccountTierReason,
  type AdminUserItem,
  type AutomationPauseState,
  type QuotaBucket,
  type UserQuotasResponse,
} from "@/services/adminApi";
import {
  formatRelativeFr,
  pluginIconColor,
  premiumLabel,
  seniorityLabel,
} from "@/lib/pluginStatusBadge";
import { resetUserQuotas } from "@/services/quotaConfigApi";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";

const BUCKET_LABELS: Record<QuotaBucket, string> = {
  invitation: "Invitations",
  message: "Messages",
  scrape: "Profils scannés (candidat / école / entreprise)",
  search: "Recherches LinkedIn",
};

const TIER_REASON_LABELS: Record<AccountTierReason, string> = {
  verified: "Profil LinkedIn vérifié",
  premium: "Compte Premium / Recruiter / Sales",
  attached_90d: "Compte attaché depuis ≥ 90 j",
  linkedin_profile_age: "Compte LinkedIn ancien (≥ 7 ans)",
  new: "Compte récent (quotas réduits)",
};

function tierIcon(reason: AccountTierReason) {
  if (reason === "verified") return BadgeCheck;
  if (reason === "premium") return Crown;
  if (reason === "attached_90d") return Sparkles;
  if (reason === "linkedin_profile_age") return Sparkles;
  return ShieldQuestion;
}

const MONTHS_FR = [
  "janvier", "fevrier", "mars", "avril", "mai", "juin",
  "juillet", "aout", "septembre", "octobre", "novembre", "decembre",
];

function formatMonthYear(month?: number | null, year?: number | null): string | null {
  if (!month || !year || month < 1 || month > 12) return null;
  return `${MONTHS_FR[month - 1]} ${year}`;
}

function freshnessLabel(value?: string | null): string | null {
  if (value === "lt_1y") return "à jour (< 1 an)";
  if (value === "gt_1y") return "> 1 an";
  return null;
}

function RecruiterTrustBlock({
  linkedin,
}: {
  linkedin?: import("@/services/adminApi").AdminUserLinkedIn;
}) {
  const memberSince = formatMonthYear(linkedin?.member_since_month, linkedin?.member_since_year);
  const contactFresh = freshnessLabel(linkedin?.contact_info_fresh);
  const photoFresh = freshnessLabel(linkedin?.photo_fresh);
  const verifications = linkedin?.verifications ?? [];

  if (!memberSince && !contactFresh && !photoFresh && verifications.length === 0) return null;

  return (
    <div className="pt-3 mt-2 border-t space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        À propos de ce membre
      </p>
      {memberSince && (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Membre LinkedIn depuis</span>
          <span className="font-medium">{memberSince}</span>
        </div>
      )}
      {contactFresh && (
        <div className="text-sm text-muted-foreground">
          Coordonnées <span className="text-foreground">{contactFresh}</span>
        </div>
      )}
      {photoFresh && (
        <div className="text-sm text-muted-foreground">
          Photo <span className="text-foreground">{photoFresh}</span>
        </div>
      )}
      {verifications.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground mb-1">
            Vérifications ({verifications.length})
          </p>
          <ul className="space-y-1">
            {verifications.map((v, i) => {
              const Icon = v.verification_type === "work_email" ? Mail : KeyRound;
              const label =
                v.verification_type === "work_email"
                  ? "Email pro"
                  : v.verification_type === "recruiter_license"
                    ? "Licence Recruiter"
                    : v.verification_type;
              const date = formatMonthYear(v.verified_month, v.verified_year);
              return (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Icon className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-medium">{v.company_name}</span>
                    <span className="text-muted-foreground"> — {label}</span>
                    {date && <span className="text-muted-foreground"> · {date}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quotas, setQuotas] = useState<UserQuotasResponse | null>(null);
  const [quotasError, setQuotasError] = useState<string | null>(null);
  const [resettingQuotas, setResettingQuotas] = useState(false);
  const [pause, setPause] = useState<AutomationPauseState | null>(null);
  const [pauseDraftMode, setPauseDraftMode] = useState<"none" | "scraping" | "all">("none");
  const [pauseReason, setPauseReason] = useState("");
  const [pauseDuration, setPauseDuration] = useState<"1h" | "24h" | "7d" | "indef">("indef");
  const [savingPause, setSavingPause] = useState(false);
  const { toast } = useToast();

  // Contrôle du plugin (kill-switch v1) — mode 'scraping' coupe le sourcing
  // (campagnes + checks continuent), mode 'all' coupe tout. Couvre les plugins
  // authentifiés (colonne DB) ET la flotte anonyme (miroir Redis par compte).
  const PAUSE_DURATION_HOURS: Record<string, number> = { "1h": 1, "24h": 24, "7d": 168 };

  const handleApplyPause = async () => {
    if (!userId) return;
    const target = pauseDraftMode;
    if (target !== "none" && !pauseReason.trim()) {
      toast({
        title: "Raison requise",
        description: "Indiquez la raison de la suspension (note interne, jamais montrée au client).",
        variant: "destructive",
      });
      return;
    }
    const ok = await confirmDialog({
      title:
        target === "none"
          ? "Réactiver l'automatisation"
          : target === "scraping"
            ? "Suspendre le sourcing du compte"
            : "Tout suspendre pour ce compte",
      description:
        target === "none"
          ? "Les plugins de ce compte recevront à nouveau des jobs. Les files accumulées repartent immédiatement (bornées par les quotas)."
          : target === "scraping"
            ? "Coupe les enrichissements (candidats, écoles, entreprises), recherches et offres d'emploi pour TOUS les plugins du compte — authentifiés ET anonymes. Les invitations, messages et checks de campagne continuent."
            : "Coupe TOUT : sourcing, invitations, messages et checks de campagne, pour tous les plugins du compte. Les campagnes en cours seront figées tant que la pause dure.",
      confirmLabel: target === "none" ? "Réactiver" : "Suspendre",
      destructive: target !== "none",
    });
    if (!ok) return;
    const until =
      target !== "none" && pauseDuration !== "indef"
        ? new Date(Date.now() + PAUSE_DURATION_HOURS[pauseDuration] * 3600_000).toISOString()
        : null;
    setSavingPause(true);
    try {
      const state = await setAutomationPause(
        userId,
        { mode: target, reason: target === "none" ? null : pauseReason.trim(), until },
        navigate
      );
      setPause(state);
      setPauseDraftMode(state.mode ?? "none");
      setPauseReason("");
      if (user) {
        setUser({
          ...user,
          scraping_disabled: state.scraping_disabled_legacy,
          scraping_disabled_at: state.at,
          scraping_disabled_by: state.by,
        });
      }
      toast({
        title:
          target === "none"
            ? "Automatisation réactivée"
            : target === "scraping"
              ? "Sourcing suspendu"
              : "Automatisation entièrement suspendue",
        description:
          target === "none"
            ? "Les plugins de ce compte recevront à nouveau des jobs."
            : `${state.account_keys_mirrored.length} clé(s) miroir posée(s) pour la flotte anonyme.`,
      });
    } catch (e) {
      toast({
        title: "Erreur",
        description: e instanceof Error ? e.message : "Mise à jour impossible",
        variant: "destructive",
      });
    } finally {
      setSavingPause(false);
    }
  };

  // Charge l'état de pause (best-effort : la card affiche le legacy flag si
  // l'endpoint échoue).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const state = await getAutomationPause(userId, navigate);
        if (cancelled) return;
        setPause(state);
        setPauseDraftMode(state.mode ?? "none");
      } catch {
        /* best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, navigate]);

  const handleResetUserQuotas = async () => {
    if (!userId) return;
    const ok = await confirmDialog({
      title: "Reset des quotas du compte",
      description:
        "Vide les compteurs anti-détection glissants (Redis) de ce compte (invitations, messages, scrape, recherches). Débloque immédiatement le compte. Action irréversible.",
      confirmLabel: "Reset",
      destructive: true,
    });
    if (!ok) return;
    setResettingQuotas(true);
    try {
      await resetUserQuotas(userId, null, navigate);
      const refreshed = await getUserQuotas(userId, navigate);
      setQuotas(refreshed);
      toast({ title: "Quotas réinitialisés", description: "Compteurs glissants vidés." });
    } catch (e) {
      toast({
        title: "Erreur",
        description: e instanceof Error ? e.message : "Reset impossible",
        variant: "destructive",
      });
    } finally {
      setResettingQuotas(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listUsers(navigate);
        if (cancelled) return;
        const found = (data.items || []).find((u) => u.user_id === userId) || null;
        setUser(found);
        if (!found) setError("Utilisateur introuvable");
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, navigate]);

  // Charge les quotas en parallele (8 lectures Redis) — endpoint dedie pour eviter
  // de plomber la liste users (couterait 8 x N users).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getUserQuotas(userId, navigate);
        if (!cancelled) setQuotas(data);
      } catch (e: unknown) {
        if (!cancelled) setQuotasError(e instanceof Error ? e.message : "Erreur inconnue");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, navigate]);

  const linkedin = user?.linkedin;
  const plugin = user?.plugin;
  const profileUrl = linkedin?.account_urn
    ? null
    : null; // pas d'URL stable depuis l'URN (on attend public_identifier en payload futur)

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Button variant="ghost" size="sm" asChild>
              <Link to="/users">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Utilisateurs
              </Link>
            </Button>
            <h1 className="text-sm font-medium">
              {user?.profile?.first_name || ""} {user?.profile?.last_name || userId}
            </h1>
          </div>
        </header>

        <div className="space-y-4 p-4">
          {loading && <p className="text-sm text-muted-foreground">Chargement…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {!loading && user && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Profil</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">Email&nbsp;: </span>
                    {user.profile?.email || "—"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Inscription&nbsp;: </span>
                    {user.registration_date
                      ? new Date(user.registration_date).toLocaleString()
                      : "—"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Abonnement&nbsp;: </span>
                    <span className="uppercase">{user.subscription_type}</span>
                  </div>
                  {user.organization && (
                    <div>
                      <span className="text-muted-foreground">Organisation&nbsp;: </span>
                      <Link
                        to={`/organizations/${user.organization.id}`}
                        className="text-primary hover:underline"
                      >
                        {user.organization.name}
                      </Link>
                      {user.organization.is_business && user.organization.tier && (
                        <span
                          className={`ml-2 rounded px-2 py-0.5 text-xs font-medium ${tierBadgeColor(user.organization.tier)}`}
                        >
                          {tierBadgeLabel(user.organization.tier)}
                        </span>
                      )}
                      <span
                        className={`ml-2 rounded px-2 py-0.5 text-xs font-medium ${roleBadgeColor(user.organization.member_role)}`}
                      >
                        {roleBadgeLabel(user.organization.member_role)}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">User ID&nbsp;: </span>
                    <code className="text-xs">{user.user_id}</code>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Plug className={`h-4 w-4 ${pluginIconColor(plugin?.is_connected, plugin?.last_seen)}`} />
                    Plugin LinkedIn
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">Statut&nbsp;: </span>
                    {plugin?.is_connected ? "En ligne" : "Hors ligne"}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Dernier heartbeat&nbsp;: </span>
                    {formatRelativeFr(plugin?.last_seen)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Version&nbsp;: </span>
                    {plugin?.version ? `v${plugin.version}` : "—"}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Contrôle du plugin</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    {pause?.mode === "all" ? (
                      <Badge variant="destructive">Tout suspendu</Badge>
                    ) : pause?.mode === "scraping" || user.scraping_disabled ? (
                      <Badge className="bg-amber-500 hover:bg-amber-600">
                        Sourcing suspendu
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-emerald-700">
                        Actif
                      </Badge>
                    )}
                    {(pause?.mode || user.scraping_disabled) && (
                      <span className="text-xs text-muted-foreground">
                        le {formatDateTime(pause?.at ?? user.scraping_disabled_at)}
                        {(pause?.by ?? user.scraping_disabled_by)
                          ? ` par ${pause?.by ?? user.scraping_disabled_by}`
                          : ""}
                        {pause?.mode
                          ? pause.until
                            ? ` — jusqu'au ${formatDateTime(pause.until)}`
                            : " — sans échéance"
                          : ""}
                      </span>
                    )}
                  </div>
                  {pause?.reason && (
                    <p className="text-xs text-muted-foreground">
                      Raison&nbsp;: {pause.reason}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    «&nbsp;Sourcing suspendu&nbsp;» coupe enrichissements, recherches et
                    offres d'emploi (invitations, messages et checks de campagne
                    continuent). «&nbsp;Tout suspendu&nbsp;» coupe aussi les actions de
                    campagne. Couvre les plugins authentifiés ET anonymes du compte
                    (miroir Redis + boucle de rattrapage horaire).
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                      <div className="text-xs text-muted-foreground">État cible</div>
                      <Select
                        value={pauseDraftMode}
                        onValueChange={(v) =>
                          setPauseDraftMode(v as "none" | "scraping" | "all")
                        }
                      >
                        <SelectTrigger className="w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Actif</SelectItem>
                          <SelectItem value="scraping">Sourcing suspendu</SelectItem>
                          <SelectItem value="all">Tout suspendu</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-muted-foreground">Durée</div>
                      <Select
                        value={pauseDuration}
                        onValueChange={(v) =>
                          setPauseDuration(v as "1h" | "24h" | "7d" | "indef")
                        }
                        disabled={pauseDraftMode === "none"}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1h">1 heure</SelectItem>
                          <SelectItem value="24h">24 heures</SelectItem>
                          <SelectItem value="7d">7 jours</SelectItem>
                          <SelectItem value="indef">Indéfinie</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleApplyPause}
                      disabled={savingPause}
                      variant={pauseDraftMode === "none" ? "default" : "destructive"}
                    >
                      {savingPause ? "Application…" : "Appliquer"}
                    </Button>
                  </div>
                  {pauseDraftMode !== "none" && (
                    <Textarea
                      value={pauseReason}
                      onChange={(e) => setPauseReason(e.target.value)}
                      placeholder="Raison (obligatoire — note interne, jamais montrée au client)"
                      maxLength={500}
                      rows={2}
                    />
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Compte LinkedIn du recruteur</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    {linkedin?.profile_verified === true ? (
                      <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-700">
                        <BadgeCheck className="h-3.5 w-3.5" /> Vérifié
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 text-amber-700">
                        <ShieldQuestion className="h-3.5 w-3.5" />{" "}
                        {linkedin?.profile_verified === false ? "Non vérifié" : "Vérification inconnue"}
                      </Badge>
                    )}
                    {linkedin?.premium === true && (
                      <Badge className="gap-1 bg-amber-500 hover:bg-amber-600">
                        <Crown className="h-3.5 w-3.5" /> {premiumLabel(linkedin.premium_type)}
                      </Badge>
                    )}
                    {linkedin?.premium === false && (
                      <Badge variant="outline">Compte gratuit</Badge>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Ancienneté&nbsp;: </span>
                    {seniorityLabel(linkedin?.profile_oldest_year)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sync identité&nbsp;: </span>
                    {formatDateTime(linkedin?.profile_health_synced_at)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sync premium&nbsp;: </span>
                    {formatDateTime(linkedin?.premium_synced_at)}
                  </div>
                  {linkedin?.account_urn && (
                    <div>
                      <span className="text-muted-foreground">URN compte&nbsp;: </span>
                      <code className="text-xs break-all">{linkedin.account_urn}</code>
                    </div>
                  )}
                  {profileUrl && (
                    <a
                      href={profileUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      Ouvrir le profil LinkedIn
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  <RecruiterTrustBlock linkedin={linkedin} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Gauge className="h-4 w-4" />
                      Quotas LinkedIn
                    </CardTitle>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleResetUserQuotas}
                      disabled={resettingQuotas}
                    >
                      {resettingQuotas ? "Reset…" : "Reset quotas"}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {quotasError && (
                    <p className="text-xs text-red-600">{quotasError}</p>
                  )}
                  {!quotasError && !quotas && (
                    <p className="text-xs text-muted-foreground">Chargement…</p>
                  )}
                  {quotas && <QuotasContent quotas={quotas} />}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

function QuotasContent({ quotas }: { quotas: UserQuotasResponse }) {
  const TierIcon = tierIcon(quotas.account_tier_reason);
  const isTrusted: boolean = quotas.account_tier === "trusted";
  const BUCKETS: QuotaBucket[] = ["invitation", "message", "scrape", "search"];

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {isTrusted ? (
          <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-700">
            <TierIcon className="h-3.5 w-3.5" /> Compte de confiance
          </Badge>
        ) : (
          <Badge variant="outline" className="gap-1 text-amber-700">
            <TierIcon className="h-3.5 w-3.5" /> Compte récent
          </Badge>
        )}
        <span className="text-xs text-muted-foreground">
          {TIER_REASON_LABELS[quotas.account_tier_reason]}
        </span>
      </div>

      <div className="space-y-2">
        {BUCKETS.map((bucket) => {
          const q = quotas.quotas[bucket];
          const hourlyPct = Math.min(100, (q.hourly.used / Math.max(q.hourly.cap, 1)) * 100);
          const dailyPct = Math.min(100, (q.daily.used / Math.max(q.daily.cap, 1)) * 100);
          return (
            <div key={bucket} className="rounded-md border p-2 space-y-1">
              <div className="text-xs font-medium">{BUCKET_LABELS[bucket]}</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] text-muted-foreground">Cette heure</div>
                  <Progress value={hourlyPct} className="h-1.5" />
                  <div className="text-[10px] tabular-nums">
                    {q.hourly.used} / {q.hourly.cap}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground">Aujourd'hui</div>
                  <Progress value={dailyPct} className="h-1.5" />
                  <div className="text-[10px] tabular-nums">
                    {q.daily.used} / {q.daily.cap}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

// Reference dummy use to silence TS6133 false positive: `AccountTier` is used as a discriminator
// indirectly via `quotas.account_tier === "trusted"`. Keep export usage check OK.
type _AccountTierKeepUsed = AccountTier;
